// §4 S2 — Costs tab (Module 04, slice 04-A). Rendered ONLY when the detail
// payload carries the `costs` key (§7 — redaction is key absence; the parent
// decides). Four accumulator cards + the §2.15 append-only entry ledger + the
// §2.18 outputs panel + the finance-only manual adjustment (§5 row 12).
//
// THE ZERO-LABOR CARD IS LABELLED, NEVER A COMPUTED 0 (§3.11): the office
// paper-traveler path writes no `labor_time_entries` by design, so a zero
// accumulator is a STRUCTURAL absence — showing "0" would claim "we measured
// no labor", which is a different and false statement. The card says no labor
// was captured on this order and where labor comes from (the floor timers,
// e31's manual adjustment). A non-zero value renders normally as money —
// driven on the real screen by M04-S12's labor-uplift leg (2026-08-08).
//
// NRV (§2.18): when the pinned BOM set no by-product value, 0 was snapshotted
// because module 09's price list does not exist — the row SAYS that, naming
// Module 09. Never a silent 0.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { ErrorState, LoadingState } from "../../../../components/states";
import { requestIdOf } from "../../../../lib/request";
import {
  useCreateCostAdjustment,
  useProductIndex,
  useWorkOrderCosts,
  type WoCostAdjustmentRequest,
  type WoCostEntry,
  type WoOutput,
  type WorkOrderCostSummary,
} from "../hooks/production-queries";
import { formatMinor, formatQty, formatTimestamp, productionErrorMessage } from "../lib/format";

const COST_TYPES = ["material", "labor", "overhead", "waste_variance"] as const;

export function CostsTab({
  workOrderId,
  costs,
  outputs,
  canAdjust,
}: {
  workOrderId: string;
  costs: WorkOrderCostSummary;
  outputs: readonly WoOutput[];
  canAdjust: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [adjustOpen, setAdjustOpen] = useState(false);

  const cardValue = (type: (typeof COST_TYPES)[number]): number => {
    switch (type) {
      case "material":
        return costs.material_minor;
      case "labor":
        return costs.labor_minor;
      case "overhead":
        return costs.overhead_minor;
      case "waste_variance":
        return costs.waste_variance_minor;
    }
  };

  return (
    <div className="wo-panel-costs">
      <h3>{t("production.detail.costs.totalsTitle")}</h3>
      <div className="cost-cards">
        {COST_TYPES.map((type) => {
          const value = cardValue(type);
          // §3.11: labor at 0 is a structural absence in 04-A, not a measured
          // zero — the card is visibly pending and says why.
          const pendingLabor = type === "labor" && value === 0;
          return (
            <div
              key={type}
              className={pendingLabor ? "cost-card is-pending" : "cost-card"}
              data-testid={`cost-card-${type}`}
            >
              <span className="cost-card-label">{t(`production.costType.${type}`)}</span>
              <span className="cost-card-value">
                {pendingLabor
                  ? t("production.detail.costs.pendingLabor")
                  : formatMinor(value, costs.currency, locale)}
              </span>
            </div>
          );
        })}
      </div>

      {canAdjust ? (
        <div className="wo-transitions">
          <Button
            onClick={() => {
              setAdjustOpen((prev) => !prev);
            }}
          >
            {t("production.detail.costs.adjust")}
          </Button>
        </div>
      ) : null}
      {adjustOpen && canAdjust ? (
        <AdjustmentForm
          workOrderId={workOrderId}
          currency={costs.currency}
          onDone={() => {
            setAdjustOpen(false);
          }}
        />
      ) : null}

      <CostLedger workOrderId={workOrderId} />

      <h3>{t("production.detail.costs.outputsTitle")}</h3>
      <OutputsTable outputs={outputs} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry ledger (§5 row 11) — cursor-paged, accumulated behind "Load more".
// ---------------------------------------------------------------------------

function CostLedger({ workOrderId }: { workOrderId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [paged, setPaged] = useState<{
    cursor: string | undefined;
    rows: readonly WoCostEntry[];
  }>({ cursor: undefined, rows: [] });
  const ledger = useWorkOrderCosts(workOrderId, paged.cursor, true);

  const rows = [...paged.rows, ...(ledger.data?.entries ?? [])];

  const columns: DataTableColumn<WoCostEntry>[] = [
    {
      key: "cost_type",
      header: t("production.detail.costs.colType"),
      render: (entry) => t(`production.costType.${entry.cost_type}`),
    },
    {
      key: "source_type",
      header: t("production.detail.costs.colSource"),
      render: (entry) => t(`production.detail.costs.source.${entry.source_type}`),
    },
    {
      key: "amount_minor",
      header: t("production.detail.costs.colAmount"),
      numeric: true,
      render: (entry) => (
        <span className={entry.amount_minor < 0 ? "amount-negative" : undefined}>
          {formatMinor(entry.amount_minor, entry.currency, locale)}
        </span>
      ),
    },
    {
      key: "entered_at",
      header: t("production.detail.costs.colAt"),
      age: true,
      render: (entry) => formatTimestamp(entry.entered_at, locale),
    },
  ];

  return (
    <div className="cost-ledger">
      <h3>{t("production.detail.costs.ledgerTitle")}</h3>
      {ledger.isPending ? <LoadingState label={t("production.common.loading")} /> : null}
      {ledger.error ? (
        <ErrorState
          title={t("production.detail.error")}
          detail={ledger.error.message}
          retryLabel={t("production.common.retry")}
          onRetry={() => void ledger.refetch()}
          requestId={requestIdOf(ledger.error)}
          requestIdLabel={t("production.common.requestId")}
        />
      ) : null}
      {ledger.data !== undefined && rows.length === 0 ? (
        <p className="production-hint">{t("production.detail.costs.ledgerEmpty")}</p>
      ) : null}
      {rows.length > 0 ? (
        <DataTable columns={columns} rows={rows} rowKey={(entry) => entry.id} empty={null} />
      ) : null}
      {ledger.data !== undefined && ledger.data.next_cursor !== null
        ? (() => {
            const page = ledger.data;
            return (
              <Button
                onClick={() => {
                  if (page.next_cursor === null) return;
                  setPaged((prev) => ({
                    cursor: page.next_cursor ?? undefined,
                    rows: [...prev.rows, ...page.entries],
                  }));
                }}
              >
                {t("production.detail.costs.loadMore")}
              </Button>
            );
          })()
        : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outputs panel (§2.18): kind, ratio, apportionment share, produced, NRV.
// ---------------------------------------------------------------------------

function OutputsTable({ outputs }: { outputs: readonly WoOutput[] }) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const locale = i18n.language;
  const products = useProductIndex(outputs.map((output) => output.product_id));

  if (outputs.length === 0) return null;

  const columns: DataTableColumn<WoOutput>[] = [
    {
      key: "product_id",
      header: term("product"),
      id: true,
      render: (output) => {
        const product = products.byId.get(output.product_id);
        return product !== undefined ? `${product.sku} · ${product.name}` : "";
      },
    },
    {
      key: "kind",
      header: t("production.detail.costs.colKind"),
      render: (output) =>
        output.kind === "finished"
          ? t("production.outputKind.finished")
          : output.kind === "co_product"
            ? term("co_product")
            : term("by_product"),
    },
    {
      key: "output_ratio",
      header: t("production.detail.costs.colRatio"),
      numeric: true,
      render: (output) => formatQty(output.output_ratio, { locale, maxDp: 6 }),
    },
    {
      key: "apportionment_pct",
      header: t("production.detail.costs.colShare"),
      numeric: true,
      render: (output) =>
        output.apportionment_pct === null
          ? t("production.common.notAvailable")
          : `${formatQty(output.apportionment_pct, { locale })}%`,
    },
    {
      key: "qty_produced",
      header: t("production.detail.costs.colProduced"),
      numeric: true,
      render: (output) => `${formatQty(output.qty_produced, { locale })} ${output.uom}`,
    },
    {
      // Polymorphic and therefore NOT `numeric`: this cell is money for a
      // by-product and a SENTENCE otherwise, and right-aligning prose reads as
      // a broken column.
      key: "nrv_minor_per_uom",
      header: t("production.detail.costs.colNrv"),
      render: (output) =>
        output.kind !== "by_product" ? (
          // Structurally inapplicable — a share-bearing output has no NRV
          // credit (this is NOT a redacted money cell).
          t("production.common.notAvailable")
        ) : output.nrv_minor_per_uom === null ||
          output.nrv_minor_per_uom === undefined ||
          output.nrv_minor_per_uom === 0 ? (
          // §2.18: 0 was snapshotted because no NRV source exists — SAY so,
          // naming Module 09; never a silent 0.
          <span className="production-hint" data-testid="no-nrv-note">
            {t("production.detail.costs.noNrv")}
          </span>
        ) : (
          formatMinor(output.nrv_minor_per_uom, output.currency, locale)
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={outputs}
      rowKey={(output) => output.id}
      empty={null}
      rowProps={(output) => ({ "data-testid": `output-${output.kind}-${output.product_id}` })}
    />
  );
}

// ---------------------------------------------------------------------------
// Manual adjustment (§5 row 12) — finance + group_admin (§7); the ledger is
// append-only, so a correction is a compensating entry, never an edit.
// ---------------------------------------------------------------------------

function AdjustmentForm({
  workOrderId,
  currency,
  onDone,
}: {
  workOrderId: string;
  /** The work order's own currency (§9.13 fixes it at creation). */
  currency: string;
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const adjust = useCreateCostAdjustment();

  const [costType, setCostType] = useState<(typeof COST_TYPES)[number]>("material");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);

  // WHAT THE NUMBER MEANS, BEFORE IT IS POSTED (builder report, 2026-08-12).
  // The field asks for `amount_minor` — the wire's signed integer minor units
  // — and said so in words, but named no CURRENCY and showed no scale: a
  // finance user correcting a material cost by 12 500 ₸ had to know, unaided,
  // that this box wanted 1250000, and a mistake here writes a permanent ledger
  // entry that can only be undone by another one. The currency rides the
  // caption and the typed value is echoed back FORMATTED, so the amount is
  // read in the same units as the four cards above it.
  const parsed = /^-?\d+$/u.test(amount.trim()) ? Number(amount.trim()) : undefined;

  return (
    <form
      className="production-panel"
      aria-label={t("production.detail.costs.adjustTitle")}
      onSubmit={(event) => {
        event.preventDefault();
        if (reason.trim().length === 0) return;
        // Signed INTEGER minor units — money is never a float (the money convention).
        if (!/^-?\d+$/u.test(amount.trim())) {
          setValidationKey("production.detail.costs.adjustAmountInvalid");
          return;
        }
        setValidationKey(undefined);
        const body: WoCostAdjustmentRequest = {
          cost_type: costType,
          amount_minor: Number(amount.trim()),
          reason: reason.trim(),
        };
        adjust.mutate({ workOrderId, body }, { onSuccess: onDone });
      }}
    >
      <h4>{t("production.detail.costs.adjustTitle")}</h4>
      <p className="production-hint">{t("production.detail.costs.adjustHint")}</p>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.costs.adjustType")}</span>
        <select
          value={costType}
          onChange={(event) => {
            setCostType(event.target.value as (typeof COST_TYPES)[number]);
          }}
        >
          {COST_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`production.costType.${type}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">
          {t("production.detail.costs.adjustAmountIn", { currency })}
        </span>
        <input
          value={amount}
          inputMode="numeric"
          required
          data-testid="cost-adjust-amount"
          onChange={(event) => {
            setAmount(event.target.value);
          }}
        />
      </label>
      <p className="production-hint" data-testid="cost-adjust-preview">
        {parsed === undefined
          ? t("production.detail.costs.adjustScale", { currency })
          : t("production.detail.costs.adjustPreview", {
              amount: formatMinor(parsed, currency, i18n.language),
            })}
      </p>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.costs.adjustReason")}</span>
        <input
          value={reason}
          required
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>
      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {adjust.isError ? (
        <p className="form-error" role="alert">
          {productionErrorMessage(t, adjust.error)}
        </p>
      ) : null}
      <div className="form-actions wo-transitions">
        {/* type="submit" explicitly — Button defaults to type="button" and this
            control submits the adjustment <form>. */}
        <Button
          type="submit"
          variant="primary"
          busy={adjust.isPending}
          busyLabel={t("production.common.saving")}
        >
          {t("production.detail.costs.adjustSubmit")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}
